/* istanbul ignore file */

/* eslint-disable react/forbid-prop-types,max-len */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import ChannelGroupType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelGroupType';
import ChannelType from '@avtechsoftware/ra-javascript-utilities/src/types/ChannelType';
import ChannelService from '@avtechsoftware/ra-javascript-utilities/src/utilities/channels/ChannelService';
import BaseTemperatureChannel from '@avtechsoftware/ra-javascript-utilities/src/classes/channels/BaseTemperatureChannel/BaseTemperatureChannel';
import { useDispatch, useSelector } from 'react-redux';
import {
  adjustFromDisplay,
  normalizeRaaChannelKey,
  parseChannelKey,
  trimRaaChannelKey,
} from '../../../../../../../utility/channelUtils';
import { CHANNEL_TYPE_ORDER, getReadingCardComponent } from '../../common/channelGroupTypeToComponentMap';
import sensorSettingsModalFactory from '../../../../modals/settings/sensorSettingsModalFactory';
import { CrudStatus as AvtechApiStatus } from '../../../../../../../utility/types/CrudStatus';
import {
  fetchSingleDevice,
  resetDeviceDataFormState,
  selectSensorUpdateStatus,
  updateSensor,
} from '../../../../../../../redux/slices/singleDeviceSlice/singleDeviceSlice';
import { selectSessionInformation } from '../../../../../../../redux/slices/sessionSlice';
import formatTimestamp from '../../../../../../../utility/formatTimestamp';

const BleSensorCard = ({
  sensor,
  isHidden,
  isMuted,
  isReadOnly,
}) => {
  const dispatch = useDispatch();
  const saveStatus = useSelector(selectSensorUpdateStatus);
  const sessionInformation = useSelector(selectSessionInformation);
  const { timezone, activeSubscriptionId } = sessionInformation;
  const [showModal, setShowModal] = React.useState(false);
  const bleGroup = sensor.channelGroups.find((group) => parseChannelKey(group.channelKey).channelGroupType === ChannelGroupType.BLUETOOTH_LE.id);
  const nonBleGroup = sensor.channelGroups.find((group) => parseChannelKey(group.channelKey).channelGroupType !== ChannelGroupType.BLUETOOTH_LE.id);
  const rssiChannel = bleGroup ? bleGroup.channels.find((channel) => parseChannelKey(channel.channelKey).channelType === ChannelType.RSSI.id) : null;
  const rssi = rssiChannel ? rssiChannel.value : 0;
  const batteryLevelChannel = bleGroup ? bleGroup.channels.find((channel) => parseChannelKey(channel.channelKey).channelType === ChannelType.BATTERY_LEVEL.id) : null;
  const powerSourceChannel = bleGroup ? bleGroup.channels.find((channel) => parseChannelKey(channel.channelKey).channelType === ChannelType.GENERIC_BOOLEAN.id) : null;
  const batteryLevel = batteryLevelChannel ? batteryLevelChannel.value : 0;
  const isPluggedIn = powerSourceChannel && powerSourceChannel.value === 1;

  useEffect(() => {
    if (saveStatus === AvtechApiStatus.SUCCESS) {
      setShowModal(false);
      dispatch(resetDeviceDataFormState());
    }
  }, [dispatch, saveStatus]);

  const handleUpdateBleGroupLabel = async (label) => {
    // we are using the ble group label for the wireless sensors label,
    // and must separately update this channelGroup when saving settings
    try {
      const payload = {
        label,
        channels: bleGroup.channels.map((channel) => ({ channelKey: channel.channelKey, adjust: channel.adjust })),
        channelKey: bleGroup.channelKey,
      };
      await dispatch(updateSensor(payload)).unwrap();
      return true;
    } catch (error) {
      throw new Error(error.message || 'Failed to update sensor label');
    }
  };

  const handleSave = async (data) => {
    try {
      const updatedChannels = nonBleGroup.channels.map((channel) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );
        let adjust = 0;
        if (data.channelData[channel.channelKey]
            && data.channelData[channel.channelKey].adjust !== undefined) {
          adjust = parseFloat(data.channelData[channel.channelKey].adjust);
        }
        const normalizedAdjust = adjustFromDisplay(
          channelInstance,
          adjust,
          sessionInformation,
        );
        return {
          channelKey: trimRaaChannelKey(channel.channelKey),
          adjust: normalizedAdjust,
        };
      });
      const payload = {
        channelKey: nonBleGroup.channelKey,
        channels: updatedChannels,
        label: nonBleGroup.label,
        isHidden: data.isHidden,
      };
      await handleUpdateBleGroupLabel(data.label);
      await dispatch(updateSensor(payload)).unwrap();
      await dispatch(fetchSingleDevice()).unwrap();
      return true;
    } catch (error) {
      // Propagate the error to the modal component
      throw new Error(error.message || 'Failed to save sensor settings');
    }
  };

  const handleCloseModal = () => setShowModal(false);

  if (nonBleGroup) {
    const ReadingCardComponent = getReadingCardComponent(nonBleGroup);
    if (ReadingCardComponent) {
      // Step 1: Sort the channels by channel type order
      const sortedChannels = [...nonBleGroup.channels]
        .filter((c) => {
          const { channelType } = parseChannelKey(c.channelKey);
          return channelType !== ChannelType.UNKNOWN.id && channelType !== ChannelType.PUSH_STATUS.id && channelType !== ChannelType.ALARM.id;
        })
        .sort((a, b) => {
          const aType = parseChannelKey(a.channelKey).channelType;
          const bType = parseChannelKey(b.channelKey).channelType;
          return (CHANNEL_TYPE_ORDER[aType] || Infinity) - (CHANNEL_TYPE_ORDER[bType] || Infinity);
        });
      // Step 2: Build readings based on sorted channels
      const readings = sortedChannels.reduce((acc, channel, i) => {
        const parsedKey = parseChannelKey(channel.channelKey);
        const channelInstance = ChannelService.getChannelGroupChannel(
          parsedKey.channelGroupType,
          parsedKey.channelIndex,
          parsedKey.channelType,
        );

        if (parsedKey.channelType === ChannelType.ANALOG_COMPUTED.id) {
          acc[`reading${i + 1}`] = {
            value: channelInstance.getDisplayValue(channel.value, { scale: sessionInformation.temperatureScale, airSpeedScale: sessionInformation.airSpeedScale }),
            units: channelInstance.getUnits({ customUnits: channel.conversion.units }),
            title: channelInstance.displayName,
            isTriggered: null,
            channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
          };
        } else {
          const scale = channelInstance instanceof BaseTemperatureChannel
            ? sessionInformation.temperatureScale
            : sessionInformation.airSpeedScale;
          acc[`reading${i + 1}`] = {
            value: channelInstance.getDisplayValue(channel.value, { scale, airSpeedScale: sessionInformation.airSpeedScale }),
            units: channelInstance.getUnits({ scale, airSpeedScale: sessionInformation.airSpeedScale }),
            title: channelInstance.displayName,
            isTriggered: null,
            channelKey: normalizeRaaChannelKey(channel.channelKey, activeSubscriptionId),
          };
        }

        return acc;
      }, {});

      const typeName = `Wireless ${ChannelService.getChannelGroupTypeByValueId(parseChannelKey(nonBleGroup.channelKey).channelGroupType).displayName}`;
      const ModalComponent = sensorSettingsModalFactory(parseChannelKey(nonBleGroup.channelKey).channelType);
      const nonBleGroupLastReceived = nonBleGroup.lastReceived;

      return (
        <div>
          <ReadingCardComponent
            sensorName={bleGroup.label}
            macAddress={parseChannelKey(nonBleGroup.channelKey).sensorMacAddress}
            typeName={typeName}
            lastUpdated={formatTimestamp(nonBleGroupLastReceived && nonBleGroupLastReceived.epochSecond, timezone, 'YYYY-MM-DD HH:mm:ss')}
            isWireless
            rssi={rssi}
            batteryLevel={batteryLevel}
            isHub={false}
            onSettingsClick={isReadOnly ? () => {} : () => setShowModal(true)}
            isPluggedIn={isPluggedIn}
            isMuted={isMuted}
            {...readings}
          />
          <ModalComponent
            show={showModal}
            handleClose={handleCloseModal}
            sensorLabel={bleGroup.label}
            channelGroup={nonBleGroup}
            onSave={handleSave}
            isHidden={isHidden}
          />
        </div>
      );
    }
  }
  return null;
};

BleSensorCard.propTypes = {
  sensor: PropTypes.object.isRequired,
  isHidden: PropTypes.bool.isRequired,
  isMuted: PropTypes.bool.isRequired,
  isReadOnly: PropTypes.bool.isRequired,
};

export default BleSensorCard;
